import React, { useState, useEffect, useCallback } from "react";
import { MaterialReactTable } from "material-react-table";
import { deleteBusiness, getBusinesss } from "@/utils";
import SidebarLayout from "@/Components/sidebar/SidebarLayout";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { chatServerInstance } from "@/helpers";
import SuccessModal from "./components/SuccessModal";
import SimpleModal from "@/Components/modal/SimpleModal";
import UpdatePaymentPlan from "./update-plan/UpdatePaymentPlan";
import UpdateSummary from "./update-plan/update-summary";
import UpdatePayment from "./update-plan/update-payment";

export const AllBusinesses = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // Page starts from 0 for Material React Table
  const [limit, setLimit] = useState(10); // Number of records per page
  const [totalCount, setTotalCount] = useState(0); // Total number of businesses
  const userId = JSON.parse(localStorage.getItem("data"))?.id;
  const [isModalOpen, setIsModalOpen] = useState(false); // business modal
  const [currentStep, setCurrentStep] = useState(1);
  const [updatePlanData, handleUpdatePlan] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [showUpdateSuccessModal, setShowUpdateSuccessModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState({
    freq: "",
    value: null,
    tiers: "",
  }); // 'monthly' or 'yearly'
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    show: false,
    business: null,
  });
  console.log({ billingCycle });
  const handleDeleteConfirmation = (business) => {
    setDeleteConfirmModal({
      show: true,
      business,
    });
  };

  // Execute delete after confirmation
  const executeDelete = async () => {
    if (!deleteConfirmModal.business) return;

    try {
      const res = await deleteBusiness(deleteConfirmModal.business.business_id);
      console.log({ res }, res?.response?.data?.success);

      if (res?.response?.data?.success === false) {
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text:
            res?.response?.data?.message ??
            "Could not delete the business. Please try again.",
          timer: 100000,
        });
      } else {
        setShowSuccessModal(true);
      }
      fetchBusinesses();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Could not delete the business. Please try again.",
        timer: 100000,
      });
    } finally {
      setDeleteConfirmModal({ show: false, business: null });
    }
  };

  useEffect(() => {
    Swal.fire({
      icon: "info",
      text: "Please scroll right and left to view more columns.",
      showConfirmButton: true,
    });
  }, []);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch businesses with pagination
      const { data, totalCount } = await getBusinesss({
        page: parseInt(page?.pageIndex || 0) + 1,
        limit: page?.pageSize || 10,
        user_id: userId,
      }); // Assuming API uses 1-based indexing
      setBusinesses(data || []);
      setTotalCount(totalCount); // Assuming the API returns the total number of businesses
    } catch (error) {
      console.error("Error fetching businesses:", error);
    }
    setLoading(false);
  }, [page?.pageIndex, page?.pageSize, userId]);
  // Fetch client secret for PaymentElement
  const fetchClientSecret = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate total (price + tax)
      // const product_id = billingCycle?.value?.product_id;
      const payload = {};
      const response = await chatServerInstance.post(
        "/business/payment/setup-intent",
        payload
      );
      setClientSecret(response?.data?.client_secret);
    } catch (err) {
      // fallback error
    } finally {
      setLoading(false);
    }
  }, [billingCycle]);
  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  useEffect(() => {
    if (!clientSecret && currentStep && currentStep === 3) {
      fetchClientSecret();
    }
  }, [clientSecret, fetchClientSecret, currentStep]);

  const columns = [
    { accessorKey: "business_id", header: "ID", size: 80 },
    { accessorKey: "name", header: "Name", size: 200 },
    {
      accessorKey: "type",
      header: "Type",
      size: 200,
      Cell: ({ row }) =>
        row?.original?.type.includes("_") &&
        row?.original?.type.split("_").length > 2
          ? row?.original?.type
              .replace(/_(?=[^_]*$)/, " /") // Replace the last underscore with a space and a slash
              .replace(/_/g, " ") // Replace all other underscores with spaces
              .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
          : row?.original?.type
              .replace(/_/g, " ") // If there's only one underscore, replace it with space
              .replace(/\b\w/g, (char) => char.toUpperCase()), // Capitalize first letter of each word
    },
    {
      accessorKey: "distance_from_runway",
      header: "Distance from runway",
      size: 200,
    },
    { accessorKey: "airport", header: "Airport", size: 150 },
    { accessorKey: "state", header: "State", size: 150 },
    { accessorKey: "country", header: "Country", size: 150 },
    {
      accessorKey: "url",
      header: "Website",
      size: 200,
      Cell: ({ row }) => (
        <a href={row.original.url} target="_blank" rel="noopener noreferrer">
          {row.original.url}
        </a>
      ),
    },
    {
      accessorKey: "plan",
      header: "Subscription Plan",
      size: 300,
      Cell: ({ row }) => row.original.package,
    },
    {
      accessorKey: "isPaid",
      header: "Subscription Status",
      size: 200,
      Cell: ({ row }) => (row.original.isPaid ? "Paid" : "Unpaid"),
    },
    // Adding delete action column
    {
      accessorKey: "delete",
      header: "Actions",
      size: 250,
      Cell: ({ row }) => {
        console.log("row.original", row.original);
        return (
          <div className="flex gap-1.5">
            {/* View Button */}
            <button
              onClick={() =>
                navigate(
                  `/business/${row.original.type}/${row.original.business_id}`
                )
              }
              className="p-2 flex flex-col items-center bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors group relative"
              title="View Details"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-500 group-hover:text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span className="text-[10px]">View</span>
            </button>

            {/* Edit Button */}
            <button
              onClick={() =>
                navigate(`/addbusiness/${row.original.business_id}`)
              }
              className="p-2 flex flex-col items-center bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors group relative"
              title="Edit"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-500 group-hover:text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <span className="text-[10px]">Edit</span>
            </button>

            {/* Delete Button */}
            <button
              onClick={() => handleDeleteConfirmation(row.original)}
              className="p-2 flex flex-col items-center bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors group relative"
              title="Delete"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-500 group-hover:text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span className="text-[10px]">Delete</span>
            </button>

            <button
              onClick={() => {
                setIsModalOpen(true);
                setCurrentStep(1);
                handleUpdatePlan(row.original);
              }}
              className="p-2 flex flex-col items-center bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors group relative"
              title="Update Payment Plan"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-500 group-hover:text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.11a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.11a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.11a1.65 1.65 0 001 1.51h.18a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9c0 .73.45 1.38 1.11 1.63a1.65 1.65 0 001.51 1h.11a2 2 0 110 4h-.11a1.65 1.65 0 00-1.51 1z"
                />
              </svg>
              <span className="text-[10px]">Update Plan</span>
            </button>
          </div>
        );
      },
    },
  ];

  const navigate = useNavigate();

  const handleAddBusiness = () => {
    navigate("/addbusiness");
  };

  const steps = [
    { label: "Select payment plan" },
    { label: "Review & Summary" },
    { label: "Payment" },
  ];

  const renderStepper = () => (
    <div className="w-full mb-8">
      <div className="flex items-center">
        {steps.map((step, idx) => (
          <React.Fragment key={step.label}>
            {/* Step circle and label */}
            <div className="flex flex-col items-center z-10">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-full border-4 transition-all duration-300 ${
                  currentStep === idx + 1
                    ? "border-[#af2322] bg-white text-[#af2322] font-bold shadow-lg"
                    : idx + 1 < currentStep
                    ? "border-[#8ec639] bg-[#8ec639] text-white"
                    : "border-gray-200 bg-white text-gray-400"
                }`}
              >
                <span className="text-lg font-semibold">{idx + 1}</span>
              </div>
              <span
                className={`mt-2 text-sm font-medium text-center max-w-[100px] ${
                  currentStep === idx + 1
                    ? "text-[#af2322]"
                    : idx + 1 < currentStep
                    ? "text-[#8ec639]"
                    : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>

            {/* Connector line (except for last step) */}
            {idx < steps.length - 1 && (
              <div
                className={`flex-1 h-1 mx-[-2px] ${
                  idx + 1 < currentStep ? "bg-[#8ec639]" : "bg-gray-200"
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderModalContent = () => {
    if (!updatePlanData) return null;
    return (
      <div
        className={`w-full ${
          currentStep === 1 ? "" : "max-w-2xl"
        } px-0 md:px-2 py-4 mx-auto overflow-y-auto`}
        style={{ maxHeight: "90vh" }}
      >
        {renderStepper()}

        {currentStep === 1 && (
          <UpdatePaymentPlan
            setCurrentStep={setCurrentStep}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
          />
        )}
        {currentStep === 2 && (
          <UpdateSummary
            setCurrentStep={setCurrentStep}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
          />
        )}
        {currentStep === 3 && clientSecret && (
          <UpdatePayment
            clientSecret={clientSecret}
            setIsModalOpen={setIsModalOpen}
            setCurrentStep={setCurrentStep}
            billingCycle={billingCycle}
            setShowSuccessModal={setShowUpdateSuccessModal}
            updatePlanData={updatePlanData}
          />
        )}
      </div>
    );
  };

  return (
    <SidebarLayout>
      <div className="relative top-16 sm:top-16 md:top-14 ls:top-[unset]">
        <div className="px-4">
          <div className="dashboard-title text-start">
            <h3>Businesses</h3>
          </div>

          {/* Add Business Button */}
          <button
            onClick={handleAddBusiness}
            className="my-2 btn btn-primary col"
          >
            Add Business
          </button>

          {/* Material React Table */}
          <MaterialReactTable
            columns={columns}
            data={businesses}
            enableColumnResizing
            enableSorting
            enableGlobalFilter
            enablePagination
            manualPagination
            rowCount={totalCount} // Adjust this for pagination info
            state={{ isLoading: loading }}
            onPaginationChange={(newPage, newLimit) => {
              setPage(newPage);
              setLimit(newLimit);
            }}
            paginationOptions={{
              pageIndex: page,
              pageSize: limit,
            }}
          />
        </div>
      </div>

      {deleteConfirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Confirm Deletion
              </h3>
              <button
                onClick={() =>
                  setDeleteConfirmModal({ show: false, business: null })
                }
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                You're about to permanently delete:
              </p>
              <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                <h4 className="font-bold text-lg text-gray-900 mb-1">
                  {deleteConfirmModal.business?.name}
                </h4>
                <p className="text-gray-600 text-sm !p-0">
                  ID: {deleteConfirmModal.business?.business_id}
                </p>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-yellow-700 !p-0">
                    This action will delete your business and any active
                    subscription associated with it. Your payment method will
                    not be billed again. Any subscription fees already paid will
                    not be refunded.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() =>
                  setDeleteConfirmModal({ show: false, business: null })
                }
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        open={showSuccessModal}
        message={
          "Your subscription has been canceled. Your payment method will not be billed again. Any subscription fees already paid will not be refunded."
        }
        onCTA={() => setShowSuccessModal(false)}
        ctaTitle="Close"
      />

      <SuccessModal
        open={showUpdateSuccessModal}
        message={"Your subscription has been updated. "}
        onCTA={() => setShowUpdateSuccessModal(false)}
        ctaTitle="Close"
      />

      {isModalOpen && (
        <SimpleModal
          isOpen={isModalOpen}
          currentStep={currentStep}
          checkStep={1}
          onClose={() => {
            setIsModalOpen(false);
            setCurrentStep(1);
            setClientSecret(null);
          }}
        >
          {renderModalContent()}
        </SimpleModal>
      )}
    </SidebarLayout>
  );
};
